import type { KioskRedReason, KioskScanRequest, KioskScanResult } from "@bw/shared/types/generated";
import { MAX_SCAN_RETRIES, classifyScanFailure, scanRetryDelayMs } from "./kioskResult";

export type KioskScanOutcome =
  | { type: "result"; result: KioskScanResult }
  | { type: "red"; reason: KioskRedReason }
  | { type: "signed_out" }
  /** Check-in was turned off for the facility (409 `kiosk_off`). */
  | { type: "off" }
  | { type: "problem" };

/**
 * One kiosk scan: a fresh kiosk-minted `scan_id`, retried under that SAME id
 * when the server asks (503 `kiosk_scan_retry`) or the response was lost, so
 * a retry can never use a second visit. Gives up to "see the front desk".
 */
export async function runKioskScan({
  token,
  scan,
  newScanId = () => crypto.randomUUID(),
  sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  maxRetries = MAX_SCAN_RETRIES,
}: {
  token: string;
  scan: (request: KioskScanRequest) => Promise<KioskScanResult>;
  newScanId?: () => string;
  sleep?: (ms: number) => Promise<void>;
  maxRetries?: number;
}): Promise<KioskScanOutcome> {
  const scanId = newScanId();
  for (let attempt = 0; ; attempt += 1) {
    try {
      return { type: "result", result: await scan({ token, scan_id: scanId }) };
    } catch (error) {
      const failure = classifyScanFailure(error);
      if (failure.kind === "signed_out") return { type: "signed_out" };
      if (failure.kind === "off") return { type: "off" };
      if (failure.kind === "red") return { type: "red", reason: failure.reason };
      if (failure.kind === "problem" || attempt >= maxRetries) return { type: "problem" };
      await sleep(scanRetryDelayMs(attempt + 1));
    }
  }
}
